/**
 * SuperTokens Auth Client for Rumio
 *
 * Handles authentication with SuperTokens for the Hetzner stack
 */

import SuperTokens from 'supertokens-web-js';
import Session from 'supertokens-web-js/recipe/session';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import ThirdParty from 'supertokens-web-js/recipe/thirdparty';

const API_DOMAIN = import.meta.env.VITE_API_URL || 'https://api.rumio.world';
// Initialize SuperTokens
SuperTokens.init({
  appInfo: {
    appName: 'Rumio',
    apiDomain: API_DOMAIN,
    apiBasePath: '/api/auth',
  },
  recipeList: [
    Session.init({
      tokenTransferMethod: "header",
    }),
    EmailPassword.init(),
    ThirdParty.init(),
  ],
});

// Clear legacy .rumio.world cookies that cause cross-project session sharing
['sAccessToken', 'sRefreshToken', 'sFrontToken', 'st-last-access-token-update'].forEach(name => {
  document.cookie = `${name}=; Domain=.rumio.world; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
});

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  referralCode: string | null;
  avatarUrl: string | null;
  tier: string;
  role: string;
  createdAt: string;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string, turnstileToken?: string): Promise<{ user: AuthUser }> {
  const response = await EmailPassword.signIn({
    formFields: [
      { id: 'email', value: email },
      { id: 'password', value: password },
    ],
    options: turnstileToken ? {
      preAPIHook: async (context) => ({
        ...context,
        requestInit: {
          ...context.requestInit,
          headers: {
            ...Object.fromEntries(new Headers(context.requestInit.headers as HeadersInit).entries()),
            "X-Turnstile-Token": turnstileToken,
          },
        },
      }),
    } : undefined,
  });

  if (response.status === 'WRONG_CREDENTIALS_ERROR') {
    throw new Error('Invalid email or password');
  }

  if (response.status !== 'OK') {
    throw new Error('Login failed');
  }

  // Fetch user profile from our API
  const user = await fetchCurrentUser();
  return { user };
}

/**
 * Sign up with email and password
 */
export async function signUp(
  email: string,
  password: string,
  name?: string,
  turnstileToken?: string
): Promise<{ user: AuthUser }> {
  const response = await EmailPassword.signUp({
    formFields: [
      { id: 'email', value: email },
      { id: 'password', value: password },
      { id: 'name', value: name || email.split('@')[0] },
    ],
    options: turnstileToken ? {
      preAPIHook: async (context) => ({
        ...context,
        requestInit: {
          ...context.requestInit,
          headers: {
            ...Object.fromEntries(new Headers(context.requestInit.headers as HeadersInit).entries()),
            "X-Turnstile-Token": turnstileToken,
          },
        },
      }),
    } : undefined,
  });

  if (response.status === 'FIELD_ERROR') {
    const error = response.formFields.find((f) => f.error)?.error || 'Validation failed';
    throw new Error(error);
  }

  if (response.status !== 'OK') {
    throw new Error('Registration failed');
  }

  // Fetch user profile from our API
  const user = await fetchCurrentUser();
  return { user };
}

/**
 * Sign in with Google OAuth
 */
export async function signInWithGoogle(): Promise<void> {
  const authUrl = await ThirdParty.getAuthorisationURLWithQueryParamsAndSetState({
    thirdPartyId: 'google',
    frontendRedirectURI: `${window.location.origin}/auth/callback`,
  });

  // Redirect to Google
  window.location.href = authUrl;
}

// Singleton promise to deduplicate concurrent calls (e.g. React double-mount)
let callbackPromise: Promise<{ user: AuthUser }> | null = null;

export async function handleAuthCallback(): Promise<{ user: AuthUser }> {
  if (callbackPromise) return callbackPromise;

  callbackPromise = (async () => {
    // If a session already exists, the callback was already processed.
    if (await Session.doesSessionExist()) {
      return { user: await fetchCurrentUser() };
    }

    try {
      const response = await ThirdParty.signInAndUp();
      if (response.status !== 'OK') {
        throw new Error('OAuth callback failed');
      }
    } catch (err) {
      // If signInAndUp fails but a session was created by a prior call
      // (e.g. duplicate call with single-use OAuth code), use that session.
      if (await Session.doesSessionExist()) {
        return { user: await fetchCurrentUser() };
      }
      throw err;
    }
    return { user: await fetchCurrentUser() };
  })();

  try {
    return await callbackPromise;
  } finally {
    callbackPromise = null;
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  await Session.signOut();
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  return await Session.doesSessionExist();
}

/**
 * Get access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  if (!(await Session.doesSessionExist())) {
    return null;
  }

  try {
    const token = await Session.getAccessToken();
    return token || null;
  } catch {
    return null;
  }
}

/**
 * Fetch current user profile from API
 */
async function fetchCurrentUser(): Promise<AuthUser> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_DOMAIN}/api/user/me`, {
    headers,
  });

  if (response.ok) {
    const data = await response.json();
    // Backend returns { user: {...} } - unwrap if needed
    const u = data.user || data;

    return {
      id: u.id || u.userId,
      email: u.email || '',
      name: u.name || 'User',
      referralCode: u.referralCode || u.referral_code || null,
      avatarUrl: u.avatarUrl || u.avatar_url || null,
      tier: u.tier || 'free',
      role: u.role || 'user',
      createdAt: u.createdAt || u.created_at || new Date().toISOString(),
    };
  }

  // If profile fetch fails (e.g. users table not set up yet), return minimal user from session
  const userId = await Session.getUserId();
  if (userId) {
    return {
      id: userId,
      email: '',
      name: 'User',
      referralCode: null,
      avatarUrl: null,
      tier: 'free',
      role: 'user',
      createdAt: new Date().toISOString(),
    };
  }

  throw new Error('Failed to fetch user profile');
}

/**
 * Add auth token to fetch requests
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();

  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('Content-Type', 'application/json');

  return fetch(url, {
    ...options,
    headers,
  });
}

export default {
  signIn,
  signUp,
  signInWithGoogle,
  handleAuthCallback,
  signOut,
  isAuthenticated,
  getAccessToken,
  fetchWithAuth,
};
